var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
/**
 * Read-only browser for the semantic catalog — the project's local ontology
 * (ADR-0007, CZNA-419 / WP-P6a).
 *
 * Three segments instead of the outgoing registry browser's two, because the model
 * now has two layers rather than one flat list:
 *
 *   Třídy      — what an authority can BE          (Person, Place, AdminMetadata, …)
 *   Vlastnosti — what it can HAVE                  (BIRTH_DATE, COORDINATES, …)
 *   Pole MARC  — how that appears in MARC 21       (108 tags, 1:1 with the tag)
 *
 * The fourth view, **Trasa**, is the one that answers the customer's actual
 * question: does the name I see in the form really come from the ontology? It puts
 * the ontology's name beside what each authority type's form-config template
 * authors, and says which of the two the form ends up showing.
 *
 * Until CZNA-461 it compared the ontology against the derived MARC catalog and
 * marked agreement per row — but since CZNA-448 that catalog is built from the
 * ontology by lowering `label: {"cs": X}` to `label: X`, so both sides were the same
 * string by construction and the column was green no matter what. The template layer
 * is the one that can actually disagree: `FormConfigCompiler.fillInLabel` only falls
 * through to the ontology when the template authors NO label, so a template that
 * authors one silently wins.
 *
 * Master-detail per CZNA-358 (`components/SearchLayout`) — the same pattern
 * the outgoing Semantic Component Registry browser used before its retirement
 * (CZNA-433).
 *
 * <p><b>The field detail explains itself</b> (CZNA-446). It absorbed the MARC catalog in
 * CZNA-424 and came out as three tables in a row with nothing saying what any of them
 * was — at Kontrola the trace table was read as a second subfield table. Each table now
 * opens its own bordered section with a "?" of its own, the whole detail carries a
 * pane-header "?" saying how the three relate, and the badges say what they assert
 * instead of only being coloured.
 *
 * <p><b>Two rows that look the same are two rows nobody can read</b> (CZNA-462). The
 * binds table showed no column for `whenIndicator`, so tag 100's two Person/GIVEN_NAME
 * binds on `$a` rendered as one row printed twice. See {@link conditionCell} for why the
 * indicator belongs on the screen while `part` — the other differentiator — does not.
 *
 * <p><b>Everything optional is treated as optional.</b> 17 of the 108 entries are
 * Aleph plumbing with no subfields and no binds, and 6 carry a `semanticStatus`
 * instead. The previous browser dereferenced `label.cs`, `semanticClass`,
 * `components.length` and `subfields.length` unguarded, so those entries would have
 * thrown; here every access is guarded and the empty case renders as a statement
 * rather than a blank.
 */
import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { Pane, PaneMenu, Button, MultiColumnList, MessageBanner, Layout, Headline, KeyValue, Accordion, AccordionSet, Select, } from '@folio/stripes/components';
import SearchLayout from '../components/SearchLayout';
import MarcCheckerModal from '../components/MarcChecker/MarcCheckerModal';
import HelpTooltip from '../components/HelpTooltip';
import PaneHelpButton from '../components/PaneHelpButton';
import OntologySourceJson from './OntologySourceJson';
import OntologyMapView from './OntologyMapView';
import { useSemanticCatalog, useSemanticCatalogTrace, useSemanticCatalogTraceSummary, } from '../hooks/useSemanticCatalog';
import { TableLoadingSkeleton } from '../components/TableStates';
import { COVERAGE_KINDS, coverageColor, coverageOf, } from './semanticCatalogCoverage';
import { noSubfieldsReason } from './semanticCatalogFieldKind';
import { badgeStyle } from './semanticCatalogBadges';
import SubfieldTraceTable from './SubfieldTraceTable';
import LabelTraceSummaryTile, { bucketLabelId } from './LabelTraceSummaryTile';
import { ANY_DISCREPANCY, DISCREPANCY_KINDS, SCOPED_OVERRIDE, bucketFieldCounts, fieldInBucket, } from './labelTraceSummary';
var groupColor = function (group) {
    if (group === 'entity')
        return '#2c5cc5';
    if (group === 'record')
        return '#5a6b7d';
    return '#7a4fa3';
};
var stewardshipColor = function (value) {
    if (value === 'aligned')
        return '#128000';
    if (value === 'mirrored')
        return '#b06000';
    return '#5a6b7d';
};
var DEFAULT_SORT = {
    classes: { column: 'label', dir: 'asc' },
    properties: { column: 'label', dir: 'asc' },
    // Fields keep tag order, which is the order a cataloguer already thinks in.
    fields: { column: 'tag', dir: 'asc' },
    // The map is a diagram, not a list — the entry exists only because the record is
    // exhaustive over Segment, and nothing reads it.
    map: { column: 'label', dir: 'asc' },
};
/** Multi-valued columns (domain, tags, alignment) are deliberately not sortable. */
var SORTABLE = {
    classes: ['label', 'group', 'properties'],
    properties: ['label', 'valueType', 'cardinality'],
    fields: ['tag', 'label', 'coverage', 'binds'],
    map: [],
};
/** Czech collation: `Č` belongs after `C`, and `numeric` keeps 100 < 1000. */
var collator = new Intl.Collator('cs', { sensitivity: 'base', numeric: true });
function sortRows(rows, state, keyOf) {
    var sorted = __spreadArray([], rows, true).sort(function (a, b) {
        var ka = keyOf(a);
        var kb = keyOf(b);
        var cmp = (typeof ka === 'number' && typeof kb === 'number')
            ? ka - kb
            : collator.compare(String(ka), String(kb));
        return cmp;
    });
    return state.dir === 'asc' ? sorted : sorted.reverse();
}
/**
 * Every alignment target of a class, across vocabularies — `bf:Topic, skos:Concept`.
 * The targets are CURIEs, so the prefix already names the vocabulary (CZNA-434).
 */
var alignmentTargets = function (alignments) { return (Object.values(alignments !== null && alignments !== void 0 ? alignments : {}).flat().join(', ')); };
/**
 * One `vocabulary: target, target` group per vocabulary, groups separated by `; ` so a
 * multi-target vocabulary stays readable as one group — `geo: geo:lat, geo:long`
 * (CZNA-434). Empty when the concept is ours.
 */
var alignmentGroups = function (alignments) { return (Object.entries(alignments !== null && alignments !== void 0 ? alignments : {}).map(function (_a) {
    var vocabulary = _a[0], targets = _a[1];
    return "".concat(vocabulary, ": ").concat(targets.join(', '));
}).join('; ')); };
/** Allowed indicator values, with the blank rendered as `#` (MARC convention). */
var indAllowed = function (values) { return (values && values.length > 0 ? values.map(function (v) { return (v === ' ' ? '#' : v); }).join(', ') : '—'); };
/**
 * The identifier of a class or property, shown as a SECONDARY note beside its Czech
 * name (CZNA-425). The name leads because this is a screen the customer reads; the
 * identifier stays visible because we quote it in tickets, in questions to NK and in
 * any future export, so hiding it would cost more than it saves.
 */
var secondaryId = function (id) { return (React.createElement("span", { style: { color: '#666', fontFamily: 'monospace', fontSize: '0.8rem', marginLeft: '0.5rem' } }, id)); };
/**
 * A named cell that renders "Czech name  identifier".
 */
var namedCell = function (label, id) { return (React.createElement(React.Fragment, null,
    React.createElement("strong", null, label || id),
    label ? secondaryId(id) : null)); };
/**
 * Where in MARC a bind sits: `$a` or `$d$e$f$g`.
 *
 * <p>`part` and `encoding` describe how our parser splits a value
 * (`beforeComma`, `parsedFromA`, `parenColonStripped`, …). That is debugging
 * information, not something a cataloguer needs, so it is not shown here
 * (CZNA-425 hid it behind a toggle; CZNA-506 dropped the toggle — see the
 * note on the binds section below).
 */
var anchorOf = function (bind) {
    var _a;
    if ((_a = bind.subfields) === null || _a === void 0 ? void 0 : _a.length)
        return bind.subfields.map(function (c) { return "$".concat(c); }).join('');
    if (!bind.subfield)
        return '—';
    return "$".concat(bind.subfield);
};
/**
 * When a bind applies (CZNA-462).
 *
 * <p>Several binds on one tag can name the same subfield, class and property and still
 * be different rules, because an indicator picks between them. Tag 100 `$a` carries two
 * such Person/GIVEN_NAME binds — `ind1=1` (the given name is the part after the comma
 * in "Surname, Given") and `ind1=0` (the whole value is a given name). Every column the
 * binds table showed was equal for those two, so the table printed what looked like a
 * duplicated row and gave the reader no way to tell it was two rules.
 *
 * <p>`part` — the parser's other differentiator — stays off this screen: it is mechanics
 * and CZNA-506 removed the toggle that revealed it. The indicator is not mechanics. The
 * field detail already prints `ind1: 0, 1, 3` a few lines above this table, and the
 * cataloguer types that value into the form, so "applies to ind1 = 0" is in the language
 * the screen already speaks.
 *
 * <p>The indicator's own meaning comes with it where the catalog knows it
 * (`ind1 = 1 (Příjmení)`); 2 of the 8 conditional binds sit on a tag whose `options`
 * are incomplete, and those render as the bare value rather than as a blank.
 */
var conditionCell = function (bind, indicators) {
    var _a;
    var conditions = Object.entries((_a = bind.whenIndicator) !== null && _a !== void 0 ? _a : {});
    if (!conditions.length) {
        // Named, not left blank: 326 of the 334 binds are unconditional, and an em dash in
        // a column called "Applies to" reads as "we do not know" rather than "always".
        return (React.createElement("span", { style: { color: '#666' } },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.semanticCatalog.bind.always" })));
    }
    return (React.createElement(React.Fragment, null, conditions.map(function (_a) {
        var _b, _c;
        var indicator = _a[0], values = _a[1];
        var options = (_c = (_b = indicators === null || indicators === void 0 ? void 0 : indicators[indicator]) === null || _b === void 0 ? void 0 : _b.options) !== null && _c !== void 0 ? _c : [];
        return (React.createElement("div", { key: indicator },
            React.createElement("code", null, "".concat(indicator, " = ")),
            values.map(function (value, index) {
                var _a;
                var meaning = (_a = options.find(function (option) { return option.value === value; })) === null || _a === void 0 ? void 0 : _a.label;
                return (
                // The value pairs with ITS OWN meaning rather than two parallel lists,
                // so a partially-described indicator cannot shift the pairing.
                React.createElement(React.Fragment, { key: value },
                    index > 0 ? ', ' : null,
                    React.createElement("code", null, value === ' ' ? '#' : value),
                    meaning ? React.createElement("span", { style: { color: '#666' } }, " (".concat(meaning, ")")) : null));
            })));
    })));
};
/** Help text for the detail pane lives under one prefix, per the help-kit convention. */
var HELP = 'ui-czna-authorities.semanticCatalog.help';
var DetailSection = function (_a) {
    var titleId = _a.titleId, helpKey = _a.helpKey, toolbar = _a.toolbar, children = _a.children;
    return (React.createElement("section", { "data-testid": "ontology-section-".concat(helpKey), style: { marginTop: '1.75rem', paddingTop: '0.75rem', borderTop: '1px solid #d8d8d8' } },
        React.createElement("div", { style: { display: 'flex', alignItems: 'center', flexWrap: 'wrap' } },
            React.createElement(Headline, { size: "medium", margin: "none" },
                React.createElement(FormattedMessage, { id: titleId })),
            React.createElement(HelpTooltip, { messageId: "".concat(HELP, ".").concat(helpKey), testKey: helpKey, idPrefix: "ontology-help" })),
        toolbar && (React.createElement("div", { "data-testid": "ontology-toolbar-".concat(helpKey), style: { margin: '0.5rem 0' } }, toolbar)),
        React.createElement("div", { style: { marginTop: '0.5rem' } }, children)));
};
/**
 * A coloured badge with its own "?" (CZNA-446). "uzavřený seznam podpolí" is a claim
 * with consequences — no subfield outside the table may be entered — and as a bare
 * coloured chip it said none of that.
 */
var badgeWithHelp = function (color, labelId, helpKey) { return (React.createElement("span", { style: { display: 'inline-flex', alignItems: 'center', marginRight: '10px' } },
    React.createElement("span", { style: badgeStyle(color) },
        React.createElement(FormattedMessage, { id: labelId })),
    React.createElement(HelpTooltip, { messageId: "".concat(HELP, ".").concat(helpKey), testKey: helpKey, idPrefix: "ontology-help" }))); };
var SemanticCatalogBrowser = function (_a) {
    var _b, _c, _d, _f, _g, _h, _j;
    var match = _a.match;
    var intl = useIntl();
    var history = useHistory();
    var _k = useState(''), query = _k[0], setQuery = _k[1];
    var _l = useState(true), filterPaneIsVisible = _l[0], setFilterPaneIsVisible = _l[1];
    var _m = useState(false), checkerOpen = _m[0], setCheckerOpen = _m[1];
    /**
     * Class the Properties / MARC-fields list is narrowed to; '' = all (CZNA-426).
     * "What can a Person have?" is the question a cataloguer actually brings to the
     * ontology, and a flat list of 78 properties cannot answer it. 29 of those 78 apply
     * to more than one class, so the right lens is a FILTER — reorganising the list into
     * per-class groups would have to duplicate a third of it.
     */
    var _o = useState(''), classFilter = _o[0], setClassFilter = _o[1];
    /**
     * The map's own class, deliberately NOT `classFilter`. Sharing it meant picking
     * `Místo` on the map silently narrowed the Properties and Fields lists — and the map
     * hides the filter accordion, so there was no visible control to clear it again.
     */
    var _p = useState(''), mapClass = _p[0], setMapClass = _p[1];
    /**
     * Coverage state the MARC-fields list is narrowed to; '' = all (CZNA-429). This is
     * what makes the tooltip's claim checkable: the reader can list exactly the five
     * fields we are waiting on instead of clicking through 108.
     */
    var _q = useState(''), coverageFilter = _q[0], setCoverageFilter = _q[1];
    /**
     * Label-trace bucket the MARC-fields list is narrowed to; '' = all (CZNA-547). What
     * the tally's numbers click through to — "which fields have a name the form shows
     * differently from the ontology?" answered by the list, not by opening 105 details.
     */
    var _r = useState(''), traceFilter = _r[0], setTraceFilter = _r[1];
    /** Per-segment, because the segments do not share a single column. */
    var _s = useState(DEFAULT_SORT), sort = _s[0], setSort = _s[1];
    /**
     * Renders an enum value from the artifact in the customer's language (CZNA-425).
     * Falls back to the raw value rather than a blank: a value we forgot to translate
     * must still be readable and must look conspicuous enough to get reported.
     */
    var term = useCallback(function (kind, value) {
        if (!value)
            return '—';
        var id = "ui-czna-authorities.semanticCatalog.".concat(kind, ".").concat(value);
        var translated = intl.formatMessage({ id: id, defaultMessage: value });
        return translated === id ? value : translated;
    }, [intl]);
    var _t = useSemanticCatalog(), data = _t.data, isLoading = _t.isLoading, isError = _t.isError;
    var onProperties = !!useRouteMatch("".concat(match.path, "/properties"));
    var onFields = !!useRouteMatch("".concat(match.path, "/fields"));
    var onMap = !!useRouteMatch("".concat(match.path, "/map"));
    var segment = onProperties
        ? 'properties'
        : (onFields ? 'fields' : (onMap ? 'map' : 'classes'));
    var classMatch = useRouteMatch("".concat(match.path, "/classes/:name"));
    var propertyMatch = useRouteMatch("".concat(match.path, "/properties/:name"));
    var fieldMatch = useRouteMatch("".concat(match.path, "/fields/:tag"));
    var selectedClass = (_b = classMatch === null || classMatch === void 0 ? void 0 : classMatch.params.name) !== null && _b !== void 0 ? _b : null;
    var selectedProperty = (_c = propertyMatch === null || propertyMatch === void 0 ? void 0 : propertyMatch.params.name) !== null && _c !== void 0 ? _c : null;
    var selectedTag = (_d = fieldMatch === null || fieldMatch === void 0 ? void 0 : fieldMatch.params.tag) !== null && _d !== void 0 ? _d : null;
    var trace = useSemanticCatalogTrace(selectedTag).data;
    // Only the fields segment shows the tally; the other segments must not pay for it.
    var _u = useSemanticCatalogTraceSummary(onFields), traceSummary = _u.data, traceSummaryLoading = _u.isLoading, traceSummaryError = _u.isError;
    var switchSegment = useCallback(function (next) {
        history.push("".concat(match.path, "/").concat(next));
    }, [history, match]);
    var open = useCallback(function (seg, key) {
        history.push("".concat(match.path, "/").concat(seg, "/").concat(encodeURIComponent(key)));
    }, [history, match.path]);
    var closeDetail = useCallback(function () {
        history.push("".concat(match.path, "/").concat(segment));
    }, [history, match.path, segment]);
    var activeSort = sort[segment];
    var handleSort = useCallback(function (_e, meta) {
        var column = meta.name;
        if (!SORTABLE[segment].includes(column))
            return;
        setSort(function (prev) {
            var _a;
            return (__assign(__assign({}, prev), (_a = {}, _a[segment] = prev[segment].column === column
                ? { column: column, dir: prev[segment].dir === 'asc' ? 'desc' : 'asc' }
                : { column: column, dir: 'asc' }, _a)));
        });
    }, [segment]);
    /**
     * MCL sort props for one segment. Columns outside `SORTABLE` are marked
     * non-interactive so a header that does nothing does not look clickable.
     */
    var sortPropsFor = function (visibleColumns) { return ({
        sortedColumn: activeSort.column,
        sortDirection: activeSort.dir === 'asc' ? 'ascending' : 'descending',
        onHeaderClick: handleSort,
        nonInteractiveHeaders: visibleColumns.filter(function (c) { return !SORTABLE[segment].includes(c); }),
    }); };
    // ---- rows -------------------------------------------------------------
    var q = query.trim().toLowerCase();
    /** class -> properties whose `domain` admits it. Declared before the row memos
     *  because the classes list sorts by this count. */
    var propertiesByClass = useMemo(function () {
        var _a;
        var index = {};
        Object.entries((_a = data === null || data === void 0 ? void 0 : data.properties) !== null && _a !== void 0 ? _a : {}).forEach(function (_a) {
            var _b;
            var name = _a[0], def = _a[1];
            ((_b = def.domain) !== null && _b !== void 0 ? _b : []).forEach(function (cls) {
                var _a;
                var list = (_a = index[cls]) !== null && _a !== void 0 ? _a : (index[cls] = []);
                list.push(name);
            });
        });
        return index;
    }, [data]);
    var classRows = useMemo(function () {
        var _a;
        var classes = (_a = data === null || data === void 0 ? void 0 : data.classes) !== null && _a !== void 0 ? _a : {};
        var rows = Object.entries(classes)
            .filter(function (_a) {
            var _b, _c;
            var name = _a[0], def = _a[1];
            return !q
                || name.toLowerCase().includes(q)
                || ((_c = (_b = def.label) === null || _b === void 0 ? void 0 : _b.cs) !== null && _c !== void 0 ? _c : '').toLowerCase().includes(q);
        })
            .map(function (_a) {
            var name = _a[0], def = _a[1];
            return ({ name: name, def: def });
        });
        return sortRows(rows, sort.classes, function (row) {
            var _a, _b, _c;
            if (sort.classes.column === 'group')
                return term('group', row.def.group);
            if (sort.classes.column === 'properties')
                return ((_a = propertiesByClass[row.name]) !== null && _a !== void 0 ? _a : []).length;
            return (_c = (_b = row.def.label) === null || _b === void 0 ? void 0 : _b.cs) !== null && _c !== void 0 ? _c : row.name;
        });
    }, [data, q, sort.classes, term, propertiesByClass]);
    var propertyRows = useMemo(function () {
        var _a;
        var properties = (_a = data === null || data === void 0 ? void 0 : data.properties) !== null && _a !== void 0 ? _a : {};
        var rows = Object.entries(properties)
            .filter(function (_a) {
            var _b;
            var def = _a[1];
            return !classFilter || ((_b = def.domain) !== null && _b !== void 0 ? _b : []).includes(classFilter);
        })
            .filter(function (_a) {
            var _b, _c, _d;
            var name = _a[0], def = _a[1];
            return !q
                || name.toLowerCase().includes(q)
                || ((_c = (_b = def.label) === null || _b === void 0 ? void 0 : _b.cs) !== null && _c !== void 0 ? _c : '').toLowerCase().includes(q)
                || ((_d = def.domain) !== null && _d !== void 0 ? _d : []).some(function (c) { return c.toLowerCase().includes(q); });
        })
            .map(function (_a) {
            var name = _a[0], def = _a[1];
            return ({ name: name, def: def });
        });
        return sortRows(rows, sort.properties, function (row) {
            var _a, _b;
            if (sort.properties.column === 'valueType')
                return term('valueType', row.def.valueType);
            if (sort.properties.column === 'cardinality')
                return term('cardinality', row.def.cardinality);
            return (_b = (_a = row.def.label) === null || _a === void 0 ? void 0 : _a.cs) !== null && _b !== void 0 ? _b : row.name;
        });
    }, [data, q, classFilter, sort.properties, term]);
    var fieldRows = useMemo(function () {
        var _a;
        var fields = (_a = data === null || data === void 0 ? void 0 : data.marc21Fields) !== null && _a !== void 0 ? _a : {};
        var rows = Object.entries(fields)
            // A field belongs to a class when any of its binds names it. Aleph plumbing and
            // unresolved fields have no binds, so a class filter correctly excludes them.
            .filter(function (_a) {
            var _b;
            var entry = _a[1];
            return !classFilter
                || ((_b = entry.binds) !== null && _b !== void 0 ? _b : []).some(function (b) { return b.cls === classFilter; });
        })
            .filter(function (_a) {
            var entry = _a[1];
            return !coverageFilter || coverageOf(entry) === coverageFilter;
        })
            .filter(function (_a) {
            var _b;
            var tag = _a[0];
            return !traceFilter || fieldInBucket((_b = traceSummary === null || traceSummary === void 0 ? void 0 : traceSummary.fields) === null || _b === void 0 ? void 0 : _b[tag], traceFilter);
        })
            .filter(function (_a) {
            var _b, _c, _d;
            var tag = _a[0], entry = _a[1];
            return !q
                || tag.toLowerCase().includes(q)
                || ((_c = (_b = entry.label) === null || _b === void 0 ? void 0 : _b.cs) !== null && _c !== void 0 ? _c : '').toLowerCase().includes(q)
                || ((_d = entry.binds) !== null && _d !== void 0 ? _d : []).some(function (b) { return b.property.toLowerCase().includes(q)
                    || b.cls.toLowerCase().includes(q); });
        })
            .map(function (_a) {
            var tag = _a[0], entry = _a[1];
            return ({ tag: tag, entry: entry });
        });
        return sortRows(rows, sort.fields, function (row) {
            var _a, _b, _c;
            if (sort.fields.column === 'label')
                return (_b = (_a = row.entry.label) === null || _a === void 0 ? void 0 : _a.cs) !== null && _b !== void 0 ? _b : '';
            if (sort.fields.column === 'coverage')
                return term('coverage', coverageOf(row.entry));
            if (sort.fields.column === 'binds')
                return ((_c = row.entry.binds) !== null && _c !== void 0 ? _c : []).length;
            return row.tag;
        });
    }, [data, q, classFilter, coverageFilter, traceFilter, traceSummary, sort.fields, term]);
    /** property -> tags that project it. The reverse of `binds`, built once. */
    var tagsByProperty = useMemo(function () {
        var _a;
        var index = {};
        Object.entries((_a = data === null || data === void 0 ? void 0 : data.marc21Fields) !== null && _a !== void 0 ? _a : {}).forEach(function (_a) {
            var _b;
            var tag = _a[0], entry = _a[1];
            ((_b = entry.binds) !== null && _b !== void 0 ? _b : []).forEach(function (bind) {
                var _a;
                var list = (_a = index[bind.property]) !== null && _a !== void 0 ? _a : (index[bind.property] = []);
                if (!list.includes(tag))
                    list.push(tag);
            });
        });
        return index;
    }, [data]);
    /**
     * The tally behind the badge and the tooltip. `awaitingNk` and `outOfModel` are
     * counted apart (CZNA-429) — that separation is the whole point: the tooltip used
     * to add them together and ask NK about two fields we had already decided on.
     */
    var coverage = useMemo(function () {
        var _a;
        var fields = Object.values((_a = data === null || data === void 0 ? void 0 : data.marc21Fields) !== null && _a !== void 0 ? _a : {});
        var count = function (kind) { return fields.filter(function (f) { return coverageOf(f) === kind; }).length; };
        return {
            total: fields.length,
            bound: count('bound'),
            awaitingNk: count('awaitingNk'),
            outOfModel: count('outOfModel'),
            unbound: count('unbound'),
        };
    }, [data]);
    /**
     * Class options with a count, so the reader can see the shape of the model before
     * choosing — Osoba 31, Korporace 24, and ConceptScheme currently 0.
     */
    var classOptions = useMemo(function () {
        var _a;
        return (__spreadArray([
            { value: '', label: intl.formatMessage({ id: 'ui-czna-authorities.semanticCatalog.filter.allClasses' }) }
        ], Object.entries((_a = data === null || data === void 0 ? void 0 : data.classes) !== null && _a !== void 0 ? _a : {}).map(function (_a) {
            var _b, _c, _d;
            var name = _a[0], def = _a[1];
            return ({
                value: name,
                label: "".concat((_c = (_b = def.label) === null || _b === void 0 ? void 0 : _b.cs) !== null && _c !== void 0 ? _c : name, " (").concat(((_d = propertiesByClass[name]) !== null && _d !== void 0 ? _d : []).length, ")"),
            });
        }), true));
    }, [data, propertiesByClass, intl]);
    /**
     * Coverage options carry their count, so "how many are we waiting on?" is answered
     * by the filter itself and the answer is one click from the rows behind it.
     */
    var coverageOptions = useMemo(function () { return (__spreadArray([
        { value: '', label: intl.formatMessage({ id: 'ui-czna-authorities.semanticCatalog.filter.allCoverage' }) }
    ], COVERAGE_KINDS.map(function (kind) { return ({
        value: kind,
        label: "".concat(intl.formatMessage({ id: "ui-czna-authorities.semanticCatalog.coverageFilter.".concat(kind) }), " (").concat(coverage[kind], ")"),
    }); }), true)); }, [coverage, intl]);
    var traceCounts = useMemo(function () { return bucketFieldCounts(traceSummary); }, [traceSummary]);
    /** The tally's buckets as filter options, with the same counts the tally shows. */
    var traceOptions = useMemo(function () { return (__spreadArray([
        { value: '', label: intl.formatMessage({ id: 'ui-czna-authorities.semanticCatalog.filter.allTrace' }) }
    ], __spreadArray(__spreadArray([ANY_DISCREPANCY], DISCREPANCY_KINDS, true), [SCOPED_OVERRIDE], false).map(function (bucket) { return ({
        value: bucket,
        label: "".concat(intl.formatMessage({ id: bucketLabelId(bucket) }), " (").concat(traceCounts[bucket], ")"),
    }); }), true)); }, [traceCounts, intl]);
    // The filter only means something where rows HAVE a class; on the classes list it
    // would filter a list by itself. Coverage is a fact about a MARC field, so that
    // filter appears on the fields segment only.
    var filterContent = (segment === 'classes' || segment === 'map') ? undefined : (React.createElement(AccordionSet, null,
        React.createElement(Accordion, { label: intl.formatMessage({ id: 'ui-czna-authorities.semanticCatalog.filter.class' }), separator: false },
            React.createElement(Select, { id: "ontology-class-filter", name: "classFilter", value: classFilter, dataOptions: classOptions, onChange: function (e) { return setClassFilter(e.target.value); } })),
        segment === 'fields' && (React.createElement(Accordion, { label: intl.formatMessage({ id: 'ui-czna-authorities.semanticCatalog.filter.coverage' }), separator: false },
            React.createElement(Select, { id: "ontology-coverage-filter", name: "coverageFilter", value: coverageFilter, dataOptions: coverageOptions, onChange: function (e) { return setCoverageFilter(e.target.value); } }))),
        segment === 'fields' && (React.createElement(Accordion, { label: intl.formatMessage({ id: 'ui-czna-authorities.semanticCatalog.filter.trace' }), separator: false },
            React.createElement(Select, { id: "ontology-trace-filter", name: "traceFilter", value: traceFilter, dataOptions: traceOptions, onChange: function (e) { return setTraceFilter(e.target.value); } })))));
    // ---- chrome -----------------------------------------------------------
    var segmentSwitcher = (React.createElement("div", { style: { display: 'flex', gap: '4px', marginBottom: '0.75rem' } }, ['classes', 'properties', 'fields', 'map'].map(function (seg) { return (React.createElement(Button, { key: seg, onClick: function () { return switchSegment(seg); }, buttonStyle: segment === seg ? 'primary' : 'default', marginBottom0: true, "aria-current": segment === seg ? 'page' : undefined },
        React.createElement(FormattedMessage, { id: "ui-czna-authorities.semanticCatalog.segment.".concat(seg) }))); })));
    var lastMenu = (React.createElement(PaneMenu, null,
        React.createElement(Button, { marginBottom0: true, onClick: function () { return setCheckerOpen(true); } },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.semanticCatalog.openMarcChecker" })),
        segment === 'fields' && (React.createElement(Layout, { className: "padding-end-gutter" },
            React.createElement("span", { style: badgeStyle(coverageColor('bound')) }, intl.formatMessage({ id: 'ui-czna-authorities.semanticCatalog.coverageShort' }, coverage)),
            React.createElement(HelpTooltip, { messageId: "ui-czna-authorities.semanticCatalog.help.coverage", messageValues: coverage, testKey: "coverage", idPrefix: "ontology-help" })))));
    /**
     * CZNA-430: a class filter that legitimately matches nothing (e.g. `ConceptScheme`
     * has 0 properties in the shipped artifact) must say so, not just "nic nenalezeno" —
     * that reads as a bug report, not as a fact about the model.
     *
     * Only blame the class when it is the SOLE active filter that could actually be
     * narrowing the CURRENT segment's rows. A search term always can; the coverage
     * filter only can on the fields segment (`fieldRows` reads it, `propertyRows`
     * never does — its own accordion isn't even rendered outside fields) — round 2 of
     * blind review caught that a stale `coverageFilter` left over from a visit to
     * Fields would otherwise suppress the class message on Properties for a class
     * that filter can't touch there. With a search term or (on fields) the coverage
     * filter also active, a zero result may be caused by either of those, not by the
     * class — naming the class then would be a false claim, not a more specific true
     * one (round 1 caught the search-term case: a class with plenty of properties
     * reads as "this class has no entries here" the moment its own search text
     * matches nothing). The label-trace filter (CZNA-547) is the same kind of
     * fields-only narrowing as coverage, so it counts the same way.
     */
    var otherFilterActive = !!q || (segment === 'fields' && (!!coverageFilter || !!traceFilter));
    var emptyMessage = (classFilter && !otherFilterActive)
        ? intl.formatMessage({ id: 'ui-czna-authorities.semanticCatalog.emptyForClass' }, { class: (_j = (_h = (_g = (_f = data === null || data === void 0 ? void 0 : data.classes) === null || _f === void 0 ? void 0 : _f[classFilter]) === null || _g === void 0 ? void 0 : _g.label) === null || _h === void 0 ? void 0 : _h.cs) !== null && _j !== void 0 ? _j : classFilter })
        : intl.formatMessage({ id: 'ui-czna-authorities.semanticCatalog.empty' });
    // ---- main pane --------------------------------------------------------
    var fieldsList = function () { return (
    // @ts-expect-error - stripes MCL types require all entity keys but only visible columns are needed
    React.createElement(MultiColumnList, __assign({ id: "ontology-fields", contentData: fieldRows, visibleColumns: ['tag', 'label', 'coverage', 'binds', 'policy'], columnMapping: {
            tag: intl.formatMessage({ id: 'ui-czna-authorities.semanticCatalog.col.tag' }),
            label: intl.formatMessage({ id: 'ui-czna-authorities.semanticCatalog.col.label' }),
            coverage: intl.formatMessage({ id: 'ui-czna-authorities.semanticCatalog.col.coverage' }),
            binds: intl.formatMessage({ id: 'ui-czna-authorities.semanticCatalog.col.bindCount' }),
            policy: intl.formatMessage({ id: 'ui-czna-authorities.semanticCatalog.col.inputPolicy' }),
        }, formatter: {
            tag: function (row) { return React.createElement("strong", null, row.tag); },
            label: function (row) { var _a, _b; return (_b = (_a = row.entry.label) === null || _a === void 0 ? void 0 : _a.cs) !== null && _b !== void 0 ? _b : '—'; },
            coverage: function (row) {
                var kind = coverageOf(row.entry);
                return (React.createElement("span", { style: badgeStyle(coverageColor(kind)) },
                    React.createElement(FormattedMessage, { id: "ui-czna-authorities.semanticCatalog.coverage.".concat(kind) })));
            },
            binds: function (row) { var _a; return ((_a = row.entry.binds) !== null && _a !== void 0 ? _a : []).length; },
            policy: function (row) { return term('inputPolicy', row.entry.input_policy); },
        } }, sortPropsFor(['tag', 'label', 'coverage', 'binds', 'policy']), { onRowClick: function (_e, row) { return open('fields', row.tag); }, isEmptyMessage: emptyMessage }))); };
    var renderMain = function () {
        // CZNA-538: the ontology is one JSON of ~108 entries but arrives over the same
        // gateway; a bare dot spinner here was the wordless wait this ticket is about.
        if (isLoading)
            return React.createElement(TableLoadingSkeleton, { testId: "ontology-loading", columns: [9, 3, 3, 3], rows: 7 });
        if (isError) {
            return (React.createElement(MessageBanner, { type: "error" },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.semanticCatalog.loadError" })));
        }
        if (segment === 'map') {
            return (React.createElement(OntologyMapView, { catalog: data, className: mapClass || null, onSelectClass: setMapClass, onOpenProperty: function (name) { return open('properties', name); }, onOpenField: function (tag) { return open('fields', tag); } }));
        }
        if (segment === 'classes') {
            return (
            // @ts-expect-error - stripes MCL types require all entity keys but only visible columns are needed
            React.createElement(MultiColumnList, __assign({ id: "ontology-classes", contentData: classRows, visibleColumns: ['label', 'group', 'alignment', 'properties'], columnMapping: {
                    label: intl.formatMessage({ id: 'ui-czna-authorities.semanticCatalog.col.class' }),
                    group: intl.formatMessage({ id: 'ui-czna-authorities.semanticCatalog.col.group' }),
                    alignment: intl.formatMessage({ id: 'ui-czna-authorities.semanticCatalog.col.alignment2' }),
                    properties: intl.formatMessage({ id: 'ui-czna-authorities.semanticCatalog.col.propertyCount' }),
                }, formatter: {
                    label: function (row) { var _a; return namedCell((_a = row.def.label) === null || _a === void 0 ? void 0 : _a.cs, row.name); },
                    group: function (row) { return (React.createElement("span", { style: badgeStyle(groupColor(row.def.group)) },
                        React.createElement(FormattedMessage, { id: "ui-czna-authorities.semanticCatalog.group.".concat(row.def.group) }))); },
                    alignment: function (row) { return alignmentTargets(row.def.alignments) || '—'; },
                    properties: function (row) { var _a; return ((_a = propertiesByClass[row.name]) !== null && _a !== void 0 ? _a : []).length; },
                } }, sortPropsFor(['label', 'group', 'alignment', 'properties']), { onRowClick: function (_e, row) { return open('classes', row.name); }, isEmptyMessage: intl.formatMessage({ id: 'ui-czna-authorities.semanticCatalog.empty' }) })));
        }
        if (segment === 'properties') {
            return (
            // @ts-expect-error - stripes MCL types require all entity keys but only visible columns are needed
            React.createElement(MultiColumnList, __assign({ id: "ontology-properties", contentData: propertyRows, visibleColumns: ['label', 'valueType', 'cardinality', 'domain', 'tags'], columnMapping: {
                    label: intl.formatMessage({ id: 'ui-czna-authorities.semanticCatalog.col.property' }),
                    valueType: intl.formatMessage({ id: 'ui-czna-authorities.semanticCatalog.col.valueType' }),
                    cardinality: intl.formatMessage({ id: 'ui-czna-authorities.semanticCatalog.col.cardinality2' }),
                    domain: intl.formatMessage({ id: 'ui-czna-authorities.semanticCatalog.col.domain' }),
                    tags: intl.formatMessage({ id: 'ui-czna-authorities.semanticCatalog.col.tags' }),
                }, formatter: {
                    label: function (row) { var _a; return namedCell((_a = row.def.label) === null || _a === void 0 ? void 0 : _a.cs, row.name); },
                    valueType: function (row) { return term('valueType', row.def.valueType); },
                    cardinality: function (row) { return term('cardinality', row.def.cardinality); },
                    domain: function (row) {
                        var _a;
                        return ((_a = row.def.domain) !== null && _a !== void 0 ? _a : [])
                            .map(function (c) { var _a, _b, _c, _d; return (_d = (_c = (_b = (_a = data === null || data === void 0 ? void 0 : data.classes) === null || _a === void 0 ? void 0 : _a[c]) === null || _b === void 0 ? void 0 : _b.label) === null || _c === void 0 ? void 0 : _c.cs) !== null && _d !== void 0 ? _d : c; }).join(', ');
                    },
                    tags: function (row) { var _a; return ((_a = tagsByProperty[row.name]) !== null && _a !== void 0 ? _a : []).join(' '); },
                } }, sortPropsFor(['label', 'valueType', 'cardinality', 'domain', 'tags']), { onRowClick: function (_e, row) { return open('properties', row.name); }, isEmptyMessage: emptyMessage })));
        }
        return (React.createElement(React.Fragment, null,
            React.createElement(LabelTraceSummaryTile, { summary: traceSummary, counts: traceCounts, isLoading: traceSummaryLoading, isError: traceSummaryError, active: traceFilter, onSelect: setTraceFilter }),
            fieldsList()));
    };
    // ---- detail panes -----------------------------------------------------
    var renderBindsTable = function (binds, indicators) {
        if (!binds.length) {
            return (React.createElement("p", null,
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.semanticCatalog.noBinds" })));
        }
        return (
        // @ts-expect-error - stripes MCL types require all entity keys but only visible columns are needed
        React.createElement(MultiColumnList, { id: "ontology-binds", contentData: binds.map(function (bind, index) { return (__assign(__assign({}, bind), { _key: index })); }), 
            // `condition` sits second, next to the anchor, because it is what tells two
            // rows apart that the following columns render identically (CZNA-462) — a
            // differentiator at the far right of six columns is one the reader finds only
            // after already concluding the table repeats itself.
            visibleColumns: ['anchor', 'condition', 'cls', 'property', 'targetClass', 'qualifiers', 'flags'], columnMapping: {
                anchor: intl.formatMessage({ id: 'ui-czna-authorities.semanticCatalog.col.anchor2' }),
                condition: intl.formatMessage({ id: 'ui-czna-authorities.semanticCatalog.col.condition' }),
                cls: intl.formatMessage({ id: 'ui-czna-authorities.semanticCatalog.col.hostClass2' }),
                property: intl.formatMessage({ id: 'ui-czna-authorities.semanticCatalog.col.property' }),
                targetClass: intl.formatMessage({ id: 'ui-czna-authorities.semanticCatalog.col.targetClass2' }),
                qualifiers: intl.formatMessage({ id: 'ui-czna-authorities.semanticCatalog.col.qualifiers' }),
                flags: intl.formatMessage({ id: 'ui-czna-authorities.semanticCatalog.col.flags' }),
            }, formatter: {
                anchor: function (row) { return React.createElement("code", null, anchorOf(row)); },
                condition: function (row) { return conditionCell(row, indicators); },
                cls: function (row) { var _a, _b, _c, _d; return (_d = (_c = (_b = (_a = data === null || data === void 0 ? void 0 : data.classes) === null || _a === void 0 ? void 0 : _a[row.cls]) === null || _b === void 0 ? void 0 : _b.label) === null || _c === void 0 ? void 0 : _c.cs) !== null && _d !== void 0 ? _d : row.cls; },
                property: function (row) { var _a, _b, _c, _d; return (_d = (_c = (_b = (_a = data === null || data === void 0 ? void 0 : data.properties) === null || _a === void 0 ? void 0 : _a[row.property]) === null || _b === void 0 ? void 0 : _b.label) === null || _c === void 0 ? void 0 : _c.cs) !== null && _d !== void 0 ? _d : row.property; },
                targetClass: function (row) {
                    var _a, _b, _c, _d;
                    return (row.targetClass
                        ? (_d = (_c = (_b = (_a = data === null || data === void 0 ? void 0 : data.classes) === null || _a === void 0 ? void 0 : _a[row.targetClass]) === null || _b === void 0 ? void 0 : _b.label) === null || _c === void 0 ? void 0 : _c.cs) !== null && _d !== void 0 ? _d : row.targetClass
                        : '—');
                },
                qualifiers: function (row) {
                    var _a;
                    return ((_a = row.qualifiers) !== null && _a !== void 0 ? _a : [])
                        .map(function (qual) { return "$".concat(qual.subfield, " = ").concat(term('role', qual.role)); }).join(', ') || '—';
                },
                flags: function (row) { return [
                    row.legacy ? intl.formatMessage({ id: 'ui-czna-authorities.semanticCatalog.flag.legacy' }) : null,
                    row.supersededBy
                        ? intl.formatMessage({ id: 'ui-czna-authorities.semanticCatalog.flag.supersededBy' }, { tag: row.supersededBy })
                        : null,
                ].filter(Boolean).join(' · ') || '—'; },
            } }));
    };
    var renderClassDetail = function () {
        var _a, _b, _c, _d, _f, _g, _h;
        var def = (_a = data === null || data === void 0 ? void 0 : data.classes) === null || _a === void 0 ? void 0 : _a[selectedClass];
        if (!def) {
            return React.createElement(FormattedMessage, { id: "ui-czna-authorities.semanticCatalog.selectEntry" });
        }
        // Indices are kept because the source-JSON panel addresses each rule by its real
        // position in the file (`classDerivation[3]`); a "rules for Osoba" array would be
        // a synthetic fourth artifact nobody could look up (CZNA-473).
        var derivation = ((_b = data === null || data === void 0 ? void 0 : data.classDerivation) !== null && _b !== void 0 ? _b : [])
            .map(function (rule, index) { return ({ rule: rule, index: index }); })
            .filter(function (_a) {
            var rule = _a.rule;
            return rule.class === selectedClass;
        });
        var sourceSlices = __spreadArray([
            { path: "classes.".concat(selectedClass), value: def }
        ], derivation.map(function (_a) {
            var rule = _a.rule, index = _a.index;
            return ({ path: "classDerivation[".concat(index, "]"), value: rule });
        }), true);
        return (React.createElement(React.Fragment, null,
            React.createElement(Headline, { size: "large" }, (_d = (_c = def.label) === null || _c === void 0 ? void 0 : _c.cs) !== null && _d !== void 0 ? _d : selectedClass),
            React.createElement(KeyValue, { label: React.createElement(FormattedMessage, { id: "ui-czna-authorities.semanticCatalog.col.class" }), value: React.createElement("code", null, selectedClass) }),
            React.createElement(KeyValue, { label: React.createElement(FormattedMessage, { id: "ui-czna-authorities.semanticCatalog.col.group" }), value: React.createElement(FormattedMessage, { id: "ui-czna-authorities.semanticCatalog.group.".concat(def.group) }) }),
            React.createElement(KeyValue, { label: React.createElement(FormattedMessage, { id: "ui-czna-authorities.semanticCatalog.col.alignment" }), value: alignmentGroups(def.alignments)
                    || intl.formatMessage({ id: 'ui-czna-authorities.semanticCatalog.ownConcept' }) }),
            def.note && (React.createElement(KeyValue, { label: React.createElement(FormattedMessage, { id: "ui-czna-authorities.semanticCatalog.col.note" }), value: def.note })),
            React.createElement(Headline, { size: "medium", margin: "small" },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.semanticCatalog.detail.propertiesOfClass" })),
            ((_f = propertiesByClass[selectedClass]) !== null && _f !== void 0 ? _f : []).length > 0 ? (React.createElement(Button, { buttonStyle: "primary", marginBottom0: true, onClick: function () {
                    setClassFilter(selectedClass);
                    history.push("".concat(match.path, "/properties"));
                } },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.semanticCatalog.showPropertiesOfClass", values: { count: ((_g = propertiesByClass[selectedClass]) !== null && _g !== void 0 ? _g : []).length } }))) : (React.createElement("p", null,
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.semanticCatalog.noProperties" }))),
            derivation.length > 0 && (React.createElement(React.Fragment, null,
                React.createElement(Headline, { size: "medium", margin: "small" },
                    React.createElement(FormattedMessage, { id: "ui-czna-authorities.semanticCatalog.detail.derivation" })),
                React.createElement("ul", null, derivation.map(function (_a) {
                    var _b;
                    var rule = _a.rule, index = _a.index;
                    return (React.createElement("li", { key: index },
                        React.createElement("code", null, (_b = rule.recordType) !== null && _b !== void 0 ? _b : rule.recordTypePattern),
                        rule.indicator && " + ".concat(rule.indicator.tag, " ind1=").concat(rule.indicator.ind1),
                        rule.default && ' (výchozí)',
                        rule.status === 'planned' && ' — plánováno'));
                })))),
            React.createElement(OntologySourceJson, { slices: sourceSlices, version: (_h = data === null || data === void 0 ? void 0 : data._meta) === null || _h === void 0 ? void 0 : _h.version })));
    };
    var renderPropertyDetail = function () {
        var _a, _b, _c, _d, _f, _g, _h;
        var def = (_a = data === null || data === void 0 ? void 0 : data.properties) === null || _a === void 0 ? void 0 : _a[selectedProperty];
        if (!def) {
            return React.createElement(FormattedMessage, { id: "ui-czna-authorities.semanticCatalog.selectEntry" });
        }
        return (React.createElement(React.Fragment, null,
            React.createElement(Headline, { size: "large" }, (_c = (_b = def.label) === null || _b === void 0 ? void 0 : _b.cs) !== null && _c !== void 0 ? _c : selectedProperty),
            React.createElement(KeyValue, { label: React.createElement(FormattedMessage, { id: "ui-czna-authorities.semanticCatalog.col.property" }), value: React.createElement("code", null, selectedProperty) }),
            React.createElement(KeyValue, { label: React.createElement(FormattedMessage, { id: "ui-czna-authorities.semanticCatalog.col.valueType" }), value: term('valueType', def.valueType) }),
            React.createElement(KeyValue, { label: React.createElement(FormattedMessage, { id: "ui-czna-authorities.semanticCatalog.col.cardinality2" }), value: (React.createElement(React.Fragment, null,
                    term('cardinality', def.cardinality),
                    def.cardinality === 'singleAtATime' && (React.createElement("span", { style: { color: '#666' } },
                        ' — ',
                        React.createElement(FormattedMessage, { id: "ui-czna-authorities.semanticCatalog.singleAtATimeHint" }))))) }),
            React.createElement(KeyValue, { label: React.createElement(FormattedMessage, { id: "ui-czna-authorities.semanticCatalog.col.domain" }), value: (React.createElement(React.Fragment, null,
                    ((_d = def.domain) !== null && _d !== void 0 ? _d : []).map(function (c) { var _a, _b, _c, _d; return (_d = (_c = (_b = (_a = data === null || data === void 0 ? void 0 : data.classes) === null || _a === void 0 ? void 0 : _a[c]) === null || _b === void 0 ? void 0 : _b.label) === null || _c === void 0 ? void 0 : _c.cs) !== null && _d !== void 0 ? _d : c; }).join(', '),
                    React.createElement("div", { style: { color: '#666', fontSize: '0.85rem' } },
                        React.createElement(FormattedMessage, { id: "ui-czna-authorities.semanticCatalog.domainHint" })))) }),
            React.createElement(KeyValue, { label: React.createElement(FormattedMessage, { id: "ui-czna-authorities.semanticCatalog.col.stewardship" }), value: (React.createElement("span", { style: badgeStyle(stewardshipColor(def.stewardship)) },
                    React.createElement(FormattedMessage, { id: "ui-czna-authorities.semanticCatalog.stewardship.".concat(def.stewardship) }))) }),
            React.createElement(KeyValue, { label: React.createElement(FormattedMessage, { id: "ui-czna-authorities.semanticCatalog.col.alignment" }), value: alignmentGroups(def.alignments)
                    || intl.formatMessage({ id: 'ui-czna-authorities.semanticCatalog.ownConcept' }) }),
            def.vocabularyType && (React.createElement(KeyValue, { label: React.createElement(FormattedMessage, { id: "ui-czna-authorities.semanticCatalog.col.vocabularyType" }), value: React.createElement("code", null, def.vocabularyType) })),
            def.note && (React.createElement(KeyValue, { label: React.createElement(FormattedMessage, { id: "ui-czna-authorities.semanticCatalog.col.note" }), value: def.note })),
            React.createElement(Headline, { size: "medium", margin: "small" },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.semanticCatalog.detail.projectedBy" })),
            React.createElement("p", null,
                ((_f = tagsByProperty[selectedProperty]) !== null && _f !== void 0 ? _f : []).map(function (tag) { return (React.createElement(Button, { key: tag, buttonStyle: "link", marginBottom0: true, onClick: function () { return open('fields', tag); } }, tag)); }),
                ((_g = tagsByProperty[selectedProperty]) !== null && _g !== void 0 ? _g : []).length === 0
                    && React.createElement(FormattedMessage, { id: "ui-czna-authorities.semanticCatalog.notProjected" })),
            React.createElement(OntologySourceJson, { slices: [{ path: "properties.".concat(selectedProperty), value: def }], version: (_h = data === null || data === void 0 ? void 0 : data._meta) === null || _h === void 0 ? void 0 : _h.version })));
    };
    /**
     * The label trace's banners — the per-field counts a reader must act on, above the
     * subfield table whose rows they are about. Until CZNA-547 they headed a trace table
     * of their own; the table is gone (its rows now fold out under each subfield), the
     * banners and what they count are unchanged (CZNA-461).
     */
    var traceBanners = function () {
        var _a, _b, _c, _d, _f, _g, _h, _j, _k;
        if (!(trace === null || trace === void 0 ? void 0 : trace.found))
            return null;
        var rows = (_a = trace.rows) !== null && _a !== void 0 ? _a : [];
        if (!rows.length) {
            return (React.createElement(MessageBanner, { type: "default" },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.semanticCatalog.trace.inNoForm" })));
        }
        var gaps = (_c = (_b = trace.summary) === null || _b === void 0 ? void 0 : _b.ONTOLOGY_MISSING) !== null && _c !== void 0 ? _c : 0;
        var redundant = (_f = (_d = trace.summary) === null || _d === void 0 ? void 0 : _d.TEMPLATE_REDUNDANT) !== null && _f !== void 0 ? _f : 0;
        var blank = (_h = (_g = trace.summary) === null || _g === void 0 ? void 0 : _g.TEMPLATE_BLANK) !== null && _h !== void 0 ? _h : 0;
        var unnamed = (_k = (_j = trace.summary) === null || _j === void 0 ? void 0 : _j.NO_LABEL_ANYWHERE) !== null && _k !== void 0 ? _k : 0;
        return (React.createElement(React.Fragment, null,
            trace.dbUnavailable && (React.createElement(MessageBanner, { type: "error" },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.semanticCatalog.trace.dbUnavailable" }))),
            gaps > 0 && (React.createElement(MessageBanner, { type: "warning" },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.semanticCatalog.trace.ontologyGap", values: { count: gaps } }))),
            redundant > 0 && (React.createElement(MessageBanner, { type: "warning" },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.semanticCatalog.trace.redundantCopy", values: { count: redundant } }))),
            blank > 0 && (React.createElement(MessageBanner, { type: "error" },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.semanticCatalog.trace.blankLabel", values: { count: blank } }))),
            unnamed > 0 && (React.createElement(MessageBanner, { type: "warning" },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.semanticCatalog.trace.noLabelAnywhere", values: { count: unnamed } })))));
    };
    /**
     * The MARC subfield table, absorbed from the retired MARC-catalog browser
     * (CZNA-424), with the label trace folded into its rows (CZNA-547). `mandatory`
     * here is the catalog's meaning — "if the field is present, this subfield is
     * required" — which is NOT the same as a form field being required, and must not
     * be conflated with it.
     *
     * <p>A field without subfields still gets the table: its field-level row carries
     * the trace of the field's own name, which a control field has as much as any.
     */
    var subfieldsSection = function (entry, tag) {
        var _a, _b;
        var subfields = (_a = entry.subfields) !== null && _a !== void 0 ? _a : [];
        return (React.createElement(React.Fragment, null,
            !subfields.length && (
            // One message per cause (CZNA-446). The old single sentence offered "a control
            // field OR Aleph-internal plumbing" and left the reader to pick — and `00X` are
            // standard MARC 21, so half of that sentence was wrong on every control field.
            React.createElement(MessageBanner, { type: "default" },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.semanticCatalog.noSubfields.".concat(noSubfieldsReason(tag)) }))),
            traceBanners(),
            React.createElement(SubfieldTraceTable, { key: tag, tag: tag, fieldLabel: (_b = entry.label) === null || _b === void 0 ? void 0 : _b.cs, subfields: subfields, trace: trace })));
    };
    var renderFieldDetail = function () {
        var _a, _b, _c, _d, _f, _g, _h;
        var entry = (_a = data === null || data === void 0 ? void 0 : data.marc21Fields) === null || _a === void 0 ? void 0 : _a[selectedTag];
        if (!entry) {
            return React.createElement(FormattedMessage, { id: "ui-czna-authorities.semanticCatalog.selectEntry" });
        }
        var status = entry.semanticStatus;
        return (React.createElement(React.Fragment, null,
            React.createElement(Headline, { size: "large" },
                selectedTag,
                ' — ', (_c = (_b = entry.label) === null || _b === void 0 ? void 0 : _b.cs) !== null && _c !== void 0 ? _c : '—'),
            status && (React.createElement(MessageBanner, { type: "warning" },
                React.createElement("strong", null,
                    React.createElement(FormattedMessage, { id: "ui-czna-authorities.semanticCatalog.state.".concat(status.state) })),
                React.createElement("div", null, status.evidence),
                status.hypothesis && (React.createElement("div", null,
                    React.createElement(FormattedMessage, { id: "ui-czna-authorities.semanticCatalog.hypothesis" }), ": ".concat(status.hypothesis))),
                status.rationale && React.createElement("div", null, status.rationale),
                React.createElement("div", null,
                    React.createElement(FormattedMessage, { id: "ui-czna-authorities.semanticCatalog.owner" }), ": ".concat(status.owner)))),
            React.createElement("div", { style: { margin: '0 0 12px' } },
                entry.input_policy === 'forbidden'
                    && badgeWithHelp('#c53030', 'ui-czna-authorities.semanticCatalog.badge.forbidden', 'badgeForbidden'),
                entry.input_policy !== 'forbidden' && entry.subfields_closed
                    && badgeWithHelp('#2b6cb0', 'ui-czna-authorities.semanticCatalog.badge.closed', 'badgeClosed'),
                entry.input_policy !== 'forbidden' && !entry.subfields_closed
                    && badgeWithHelp('#718096', 'ui-czna-authorities.semanticCatalog.badge.open', 'badgeOpen'),
                entry.repeatable_default
                    && badgeWithHelp('#2f855a', 'ui-czna-authorities.semanticCatalog.badge.repeatable', 'badgeRepeatable')),
            entry.indicators && (React.createElement(KeyValue, { label: React.createElement(FormattedMessage, { id: "ui-czna-authorities.semanticCatalog.indicators" }), value: (React.createElement("span", { style: { fontFamily: 'monospace' } }, "ind1: ".concat(indAllowed((_d = entry.indicators.ind1) === null || _d === void 0 ? void 0 : _d.allowed)),
                    '   ', "ind2: ".concat(indAllowed((_f = entry.indicators.ind2) === null || _f === void 0 ? void 0 : _f.allowed)))) })),
            React.createElement(DetailSection, { titleId: "ui-czna-authorities.semanticCatalog.detail.subfields", helpKey: "subfieldsTable" }, subfieldsSection(entry, selectedTag)),
            React.createElement(DetailSection, { titleId: "ui-czna-authorities.semanticCatalog.detail.binds", helpKey: "bindsTable" }, renderBindsTable((_g = entry.binds) !== null && _g !== void 0 ? _g : [], entry.indicators)),
            React.createElement("div", { style: { marginTop: '1.75rem' } },
                React.createElement(OntologySourceJson, { slices: [{ path: "marc21Fields.".concat(selectedTag), value: entry }], version: (_h = data === null || data === void 0 ? void 0 : data._meta) === null || _h === void 0 ? void 0 : _h.version }))));
    };
    var hasDetail = !!(selectedClass || selectedProperty || selectedTag);
    /**
     * "What am I looking at" for the open detail (CZNA-446). The field detail was three
     * tables in a row and said nowhere how they relate — during Kontrola the trace table
     * was read as a second subfield table. Since CZNA-547 it is two: the trace folds out
     * under the subfield rows. It goes in the pane header rather than in a
     * banner at the top of the body for the reason CZNA-366 established: the body banner
     * is easy to miss and pushes an already-long detail further down.
     */
    var detailHelp = selectedTag ? (React.createElement("div", { "data-testid": "ontology-field-help" },
        React.createElement("p", null,
            React.createElement(FormattedMessage, { id: "".concat(HELP, ".fieldDetail.intro") })),
        React.createElement("ul", { style: { margin: 0, paddingLeft: '1.25rem' } },
            React.createElement("li", null,
                React.createElement(FormattedMessage, { id: "".concat(HELP, ".fieldDetail.subfields") })),
            React.createElement("li", null,
                React.createElement(FormattedMessage, { id: "".concat(HELP, ".fieldDetail.trace") })),
            React.createElement("li", null,
                React.createElement(FormattedMessage, { id: "".concat(HELP, ".fieldDetail.binds") }))))) : (React.createElement("p", null,
        React.createElement(FormattedMessage, { id: selectedProperty ? "".concat(HELP, ".propertyDetail") : "".concat(HELP, ".classDetail") })));
    var detailPane = hasDetail ? (React.createElement(Pane, { defaultWidth: "45%", paneTitle: React.createElement(FormattedMessage, { id: "ui-czna-authorities.semanticCatalog.detail.title" }), dismissible: true, onClose: closeDetail, lastMenu: (React.createElement(PaneMenu, null,
            React.createElement(PaneHelpButton, null, detailHelp))) },
        React.createElement("div", { style: { padding: '0 1rem 1rem' } },
            selectedClass && renderClassDetail(),
            selectedProperty && renderPropertyDetail(),
            selectedTag && renderFieldDetail()))) : undefined;
    var totalRecords = (function () {
        var _a;
        if (segment === 'classes')
            return classRows.length;
        if (segment === 'properties')
            return propertyRows.length;
        if (segment === 'fields')
            return fieldRows.length;
        // Map: the properties of the class being mapped. Reusing a list length here
        // would report a count for rows that are not on the screen.
        return ((_a = propertiesByClass[mapClass]) !== null && _a !== void 0 ? _a : []).length;
    })();
    var list = {
        filterPaneIsVisible: filterPaneIsVisible,
        toggleFilterPane: function () { return setFilterPaneIsVisible(function (visible) { return !visible; }); },
        setQuery: setQuery,
        handleClearSearch: function () { return setQuery(''); },
        query: query,
        totalRecords: totalRecords,
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(SearchLayout, { list: list, searchPlaceholder: intl.formatMessage({
                id: 'ui-czna-authorities.semanticCatalog.search.placeholder',
            }), paneTitle: React.createElement(FormattedMessage, { id: "ui-czna-authorities.semanticCatalog.title" }), lastMenu: lastMenu, subNavigation: segmentSwitcher, filterContent: filterContent, detailPane: detailPane }, renderMain()),
        React.createElement(MarcCheckerModal, { open: checkerOpen, onClose: function () { return setCheckerOpen(false); } })));
};
export default SemanticCatalogBrowser;
